import { Modal } from '@mantine/core'
import { motion } from 'framer-motion'
import { IPhoneNumber } from '../../interface'
import { formatPhoneNumber } from '../../utils'
import { ArrowRightMdIcon, CloseIcon } from '@forwardflow/icons'

interface ConfirmRealtorNumberChangeModalProps {
   isOpen: boolean
   onClose: () => void
   onConfirm: () => void
   oldNumber?: IPhoneNumber
   newNumber: IPhoneNumber
   isLoading?: boolean
}

export const ConfirmRealtorNumberChangeModal = ({
   isOpen,
   onClose,
   onConfirm,
   oldNumber,
   newNumber,
   isLoading = false,
}: ConfirmRealtorNumberChangeModalProps) => {
   return (
      <Modal
         opened={isOpen}
         onClose={onClose}
         withCloseButton={false}
         centered
         size="lg"
         padding={0}
         styles={{
            content: {
               backgroundColor: 'rgba(0, 0, 0, 0.50)',
               backdropFilter: 'blur(8px)',
               borderRadius: '12px',
            },
            body: {
               padding: 0,
            },
         }}
      >
         <div className="flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6">
               <h2 className="text-white text-xl font-medium">
                  Change Realtor Call Line
               </h2>
               <button
                  onClick={onClose}
                  className="text-white/70 hover:text-white transition-colors p-1"
               >
                  <CloseIcon className="w-5 h-5" />
               </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6 bg-[#9061DA]/10 m-6 rounded-lg">
               {/* Description */}
               <div className="p-1">
                  <p className="text-white leading-relaxed">
                     This will be the number used by Forward Flow to communicate
                     with the agents from now on.
                  </p>
               </div>

               {/* Old and New Number Comparison */}
               <div className="grid grid-cols-[1fr_auto_1fr] items-center">
                  {/* Old Number */}
                  <div className="bg-[#9061DA]/20 rounded-lg p-6 flex flex-col gap-y-2">
                     <p className="text-white text-sm font-medium">
                        Old Number
                     </p>
                     <p className="text-white text-xl font-semibold">
                        {oldNumber
                           ? formatPhoneNumber(
                                oldNumber.phoneNumber.phoneNumber
                             )
                           : 'Not Set'}
                     </p>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center justify-center">
                     <ArrowRightMdIcon className="w-5 h-5 text-white" />
                  </div>

                  {/* New Number */}
                  <div className="rounded-lg bg-[#9061DA]/10 p-6 flex flex-col gap-y-2">
                     <p className="text-white text-sm font-medium">
                        New Number
                     </p>
                     <p className="text-white text-xl font-semibold">
                        {formatPhoneNumber(newNumber.phoneNumber.phoneNumber)}
                     </p>
                  </div>
               </div>

               {/* Confirmation Message */}
               <div className="p-2">
                  <p className="text-white leading-relaxed">
                     Upon Confirmation, we will send the new contact card to all
                     the agents in your organization
                  </p>
               </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-x-3 p-6">
               <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  disabled={isLoading}
                  className="px-8 py-3 rounded-lg border border-white/20 text-white font-medium hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
               >
                  Back
               </motion.button>
               <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="px-8 py-3 rounded-lg bg-[#9061DA] hover:bg-[#7E4EC9] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-2"
               >
                  {isLoading && (
                     <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                           duration: 1,
                           repeat: Infinity,
                           ease: 'linear',
                        }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                     />
                  )}
                  {isLoading ? 'Updating...' : 'Confirm'}
               </motion.button>
            </div>
         </div>
      </Modal>
   )
}
